import classNames from "classnames";
import qs from "query-string";
import React, { useRef, useState } from "react";
import { Link as RLink, useNavigate } from "react-router-dom";

import { useMessages } from "../../context/MessagesContext";
import { useRadar } from "../../context/RadarContext";
import { useSearchParamState } from "../../hooks/use-search-param-state";
import { Tag } from "../../model";
import { RADARS, RADAR_SLUGS } from "../../radars";
import Link from "../Link/Link";
import Search from "../Search/Search";
import SiteHeader, { SiteHeaderCta } from "../SiteHeader/SiteHeader";
import TagsModal from "../TagsModal/TagsModal";
import "./Header.scss";

/**
 * Header of a radar page.
 *
 * The bar itself — wordmark, hairline, link treatment — is `SiteHeader`, the one
 * the radars home wears, so the two cannot drift apart. What is added here is
 * radar specific: the Tech / AI switcher on the left, and the page links plus
 * search on the right.
 */
export default function Header({ tags }: { pageName: string; tags: Tag[] }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { searchLabel, pageHelp, pageOverview } = useMessages();
  const { slug, descriptor } = useRadar();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchParamState, setSearchParamsState] = useSearchParamState();

  const closeSearch = () => {
    setSearchOpen(false);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleSearchSubmit = () => {
    let { tags } = searchParamState;
    tags = Array.isArray(tags) ? tags.join("|") : tags;

    navigate({
      pathname: `/${slug}/overview.html`,
      search: qs.stringify({ search: search, tags }),
    });

    setSearchOpen(false);
    setSearch("");
  };

  const handleOpenClick = () => {
    setSearchOpen(true);
    setTimeout(() => {
      searchRef?.current?.focus();
    }, 0);
  };

  const handleTagChange = (tag: Tag) => {
    const { search, tags = [] } = searchParamState;
    let newTags;

    // Toggle changed item in tags searchParam depends on type Array or String
    if (Array.isArray(tags)) {
      newTags = tags.includes(tag)
        ? tags.filter((item: string) => item !== tag)
        : [...tags, tag];
    } else {
      newTags = tags !== tag ? [tags, tag] : [];
    }

    setSearchParamsState({
      tags: newTags,
      search,
    });
  };

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const toggleModal = function () {
    setModalIsOpen(!modalIsOpen);
  };

  return (
    <>
      <SiteHeader
        nav={
          <>
            <Link pageName="index" className="site-header__link">
              {descriptor.shortName}
            </Link>

            {pageHelp && (
              <Link
                pageName="help-and-about-tech-radar"
                className="site-header__link"
              >
                How to use
              </Link>
            )}

            <Link pageName="overview" className="site-header__link">
              {pageOverview?.title || "Technologies Overview"}
            </Link>

            <div className="radar-search">
              <button
                type="button"
                className="site-header__link"
                onClick={handleOpenClick}
                aria-expanded={searchOpen}
              >
                {searchLabel || "Search"}
              </button>
              <div
                className={classNames("radar-search__panel", {
                  "is-open": searchOpen,
                })}
              >
                <Search
                  value={search}
                  onClose={closeSearch}
                  onSubmit={handleSearchSubmit}
                  onChange={handleSearchChange}
                  open={searchOpen}
                  ref={searchRef}
                />
              </div>
            </div>
          </>
        }
        controls={
          <>
            <div
              className="radar-switcher"
              role="navigation"
              aria-label="Radar"
            >
              {RADAR_SLUGS.map((radarSlug) => (
                <RLink
                  key={radarSlug}
                  to={`/${radarSlug}/index.html`}
                  className={classNames("radar-switcher__link", {
                    "is-active": radarSlug === slug,
                  })}
                  aria-current={radarSlug === slug ? "page" : undefined}
                >
                  {RADARS[radarSlug].switchLabel}
                </RLink>
              ))}
            </div>
            <SiteHeaderCta href="https://www.elyadata.com/">
              elyadata.com
            </SiteHeaderCta>
          </>
        }
      />

      {Boolean(tags.length) && (
        <TagsModal
          tags={tags}
          isOpen={modalIsOpen}
          closeModal={toggleModal}
          handleTagChange={handleTagChange}
        />
      )}
    </>
  );
}
